// Unwrapping the router's subgraph-error envelope — THE one implementation
// (ui-conventions.md #68).
//
// The supergraph router reports everything that goes wrong beneath it with the
// same sentence: `Failed to fetch from Subgraph '<name>'.` The reason lives in
// `extensions.errors[].message`, and the shell says nothing a reader can act
// on — a transport failure, an authorization denial and a validation refusal
// are byte-identical at that level.
//
// This is not theoretical. Issue #97 began as two users reporting a permanent
// "failed to fetch" on /rules; the investigation could not tell a subgraph
// that had gone away from one that had answered "requires rules.read", because
// both render as that same sentence — in the banner AND in Sentry, whose
// context showed only `extensions: {errors: "[Array]"}`. Hours went into a
// question the payload had already answered.
//
// `mutationErrorMessage` (src/modules/core/lib/afterMutation.ts) has unwrapped
// this for MUTATIONS since ticketPrint's "person N has no ticket entitlement"
// was hidden the same way. Queries never did — which is exactly the path the
// #97 users were on. Hence one shared module rather than a second copy (owner
// ruling 2026-09-05: "This seems to be a general issue across the whole WebUI
// — it should show the subgraphs' error message").
//
// Failing #34 in spirit is the thing to avoid: a backend error must surface as
// a frontend error the reader can act on, never as a shell that hides it.
import { isRateLimited, rateLimitMessage } from 'src/lib/rateLimit'

/** One GraphQL error as urql exposes it — the shape both queries and mutations
 *  see, and all this module needs. */
export interface SubgraphErrorLike {
  message: string
  extensions?: Record<string, unknown> | null | undefined
}

/** The nested messages the router wrapped, innermost first, or [] when the
 *  error carries no envelope (a plain subgraph error, or one raised by the
 *  router itself such as an unknown field). */
export function nestedSubgraphMessages(e: SubgraphErrorLike): string[] {
  const nested = e.extensions?.errors
  if (!Array.isArray(nested)) return []
  return nested
    .map((n) =>
      n && typeof n === 'object' && typeof (n as { message?: unknown }).message === 'string'
        ? (n as { message: string }).message
        : null,
    )
    .filter((m): m is string => m !== null && m.trim() !== '')
}

/** The most specific message this error carries: the subgraph's own reason
 *  when the router wrapped one, else the error's own message.
 *
 *  The nested reason always wins. `Failed to fetch from Subgraph 'rules'.`
 *  tells a reader nothing they can act on; `rules: read requires rules.read`
 *  tells them precisely what to ask for. */
export function subgraphErrorMessage(e: SubgraphErrorLike): string {
  // The router's "Rate limit exceeded" tells nobody how long to wait.
  if (isRateLimited(e)) return rateLimitMessage(e)
  const nested = nestedSubgraphMessages(e)
  return nested.length > 0 ? nested.join('; ') : e.message
}

/** The same, across every error on a failed result — for the one banner or
 *  the one Sentry title that has to stand for all of them. A partial failure
 *  must never be silently reduced to its first error. */
export function combinedErrorMessage(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null
  if (isRateLimited(error)) return rateLimitMessage(error)
  const withGql = error as { graphQLErrors?: SubgraphErrorLike[]; message?: unknown }
  if (Array.isArray(withGql.graphQLErrors) && withGql.graphQLErrors.length > 0) {
    return withGql.graphQLErrors.map(subgraphErrorMessage).join('; ')
  }
  if (typeof withGql.message === 'string') return withGql.message
  return null
}
